import React from 'react';
import { CalendarDaysIcon, ClockIcon, InfoIcon, MapPinIcon } from 'lucide-react';
import { NewTabHint } from '../ui/NewTabHint';
import { doodleCatalog } from '../../data/doodleShapes';
import type { BcmEvent } from '../../data/events';
const categoryStyles: Record<BcmEvent['category'], string> = {
  'Fall 2026': 'bg-brand-yellow text-[#3d2b00]',
  Weekly: 'bg-brand-blue text-brand-navy',
  'Coming Soon': 'bg-brand-pink text-[#3f1730]'
};

/** One supplied shape per category, so each card carries its own accent. */
const cardAccents: Record<BcmEvent['category'], string> = {
  'Fall 2026': doodleCatalog.yellow.stars[1],
  Weekly: doodleCatalog.navy.stars[4],
  'Coming Soon': doodleCatalog.lightBlue.organics[1]
};
export function EventCard({
  event
}: {
  event: BcmEvent;
}) {
  return <article className="relative flex h-full flex-col overflow-hidden rounded-[20px] border-2 border-brand-navy bg-white p-6 transition-transform duration-200 hover:-translate-y-1" data-id="element-61" data-mp="zm9tvy1u2sm4">
      <img src={cardAccents[event.category]} alt="" aria-hidden="true" draggable={false} className="pointer-events-none absolute -right-2 -top-2 h-11 w-11 rotate-12 object-contain" data-id="element-62" data-mp="1csq9q7k17bil" />
      <span className={`inline-flex w-fit items-center rounded-full px-3 py-1 font-meta text-[11px] font-semibold uppercase tracking-[0.14em] ${categoryStyles[event.category]}`} data-id="element-63" data-mp="13stefn34zeek">
        {event.category}
      </span>

      <h3 className="mt-4 font-display text-2xl font-bold leading-tight text-brand-navy" data-id="element-64" data-mp="1wel96fedahdp">
        {event.title}
      </h3>

      <dl className="mt-4 space-y-2 font-meta text-[13px] text-ink" data-id="element-65" data-mp="1mirv2rl02ftt">
        <div className="flex items-start gap-2" data-id="element-66" data-mp="1mkm5hbynapjn">
          <dt className="sr-only" data-id="element-67" data-mp="1rgte5cifef5a">Date</dt>
          <CalendarDaysIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-68" data-mp="1rjpd9460f9vb" />
          <dd data-id="element-69" data-mp="b5fnq6q17f2w">{event.date}</dd>
        </div>
        {event.time ? <div className="flex items-start gap-2" data-id="element-70" data-mp="g38ub0v8yb3p">
            <dt className="sr-only" data-id="element-71" data-mp="3s5pjfya3u5zf">Time</dt>
            <ClockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-72" data-mp="3s4fkl6vfgtrq" />
            <dd data-id="element-73" data-mp="3o56ucbzlje0x">{event.time}</dd>
          </div> : null}
        {event.locationLines ?? event.location ? <div className="flex items-start gap-2" data-id="element-74" data-mp="3jbufuuomsnlw">
            <dt className="sr-only" data-id="element-75" data-mp="3eeu90hjyjgtq">Location</dt>
            <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-76" data-mp="3ee2c93emjd1v" />
            <dd data-id="element-77" data-mp="39gsnlpz275f3">
              {event.locationLines ? event.locationLines.map(line => <span key={line} className="block" data-id="element-78" data-mp="34el8wsmuhb92">{line}</span>) : event.location}
            </dd>
          </div> : null}
        {event.locationNote ? <div className="flex items-start gap-2" data-id="element-79" data-mp="2vhauwzin780g">
            <dt className="sr-only" data-id="element-80" data-mp="ynptm1qomaof">Details</dt>
            <InfoIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-81" data-mp="ymere6cvxazf" />
            <dd data-id="element-82" data-mp="tov2mcik9ft8">{event.locationNote}</dd>
          </div> : null}
      </dl>

      <p className="mt-4 font-sans text-[15px] leading-relaxed text-ink" data-id="element-83" data-mp="116takcl3giuc">{event.description}</p>

      {event.clarification ? <p className="mt-3 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-84" data-mp="164mhx8y4viw7">
          {event.clarification}
        </p> : null}

      {event.cta || event.reference ? <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-3 pt-1" data-id="element-85" data-mp="1fvz3xjx93eub">
          {event.cta ? <a href={event.cta.href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center rounded-full border-2 border-brand-navy bg-brand-navy px-5 font-display text-sm font-semibold text-white transition-colors hover:bg-[#083a7c]" data-id="element-86" data-mp="ial08ud85hs6">
              {event.cta.label}
              <NewTabHint data-id="element-87" data-mp="23lbcqioo1kw3" />
            </a> : null}
          {event.reference ? <a href={event.reference.href} target="_blank" rel="noopener noreferrer" className="font-meta text-[13px] font-semibold text-brand-navy underline underline-offset-4 hover:no-underline" data-id="element-88" data-mp="27mn8l5y4v2cq">
              {event.reference.label}
              <NewTabHint data-id="element-89" data-mp="2g5gogl1vkxog" />
            </a> : null}
        </div> : null}
    </article>;
}